/**
 * Unit reconciliation for the carbon emission calculator.
 *
 * Wraps the npm `convert` library and extends it with domain-specific
 * "opaque" codes that the library doesn't natively support (compound units
 * like `passenger_mile`, activity units like `night` / `fte_hour`, currency
 * codes). Opaque codes are equivalence-only — never converted.
 *
 * See `plans/carbon-unit-reconciliation.md` for the end-to-end design.
 */
import { convert, getMeasureKind } from 'convert';
import { conversions, MeasureKind } from 'convert/conversions';
/**
 * Domain-specific unit codes that aren't in the `convert` library.
 * Treated as opaque identifiers: equivalence-only, never converted.
 *
 * Keep the list closed so typos surface loudly as IncompatibleUnitsError
 * instead of silently passing through.
 */
export const OPAQUE_UNIT_CODES = [
    'short_ton', // US short ton — used by EPA waste factors
    'scf', // Standard cubic feet — used by EPA natural gas
    'passenger_mile', // Compound unit — EPA air travel
    'vehicle_mile', // Compound unit — EPA fleet vehicles
    'passenger_km', // Compound unit — Climatiq passenger transport (metric)
    'vehicle_km', // Compound unit — Climatiq fleet vehicles (metric)
    'fte_hour', // FTE working hour — DEFRA homeworking
    'night', // Hotel / accommodation
    'passenger_night', // Per-passenger lodging
    'meal', // Per-meal — Climatiq food & beverage services
    'number', // Count / item — Climatiq Number unit type
    'container_km', // Climatiq ContainerOverDistance
    'tonne_km', // Climatiq WeightOverDistance (freight)
    'litre_km', // Climatiq VolumeOverDistance
    'm2_year', // Climatiq AreaOverTime
    'gb_hour', // Climatiq DataOverTime
    'km_hour', // Climatiq DistanceOverTime
    'kg_year', // Climatiq WeightOverTime
    'number_year', // Climatiq NumberOverTime
    'eur', // Spend-based (EUR)
    'usd', // Spend-based (USD)
];
// Opaque codes rendered as display labels for the dropdown.
const OPAQUE_LABELS = {
    short_ton: 'Short ton (US)',
    scf: 'Standard cubic foot',
    passenger_mile: 'Passenger-mile',
    vehicle_mile: 'Vehicle-mile',
    passenger_km: 'Passenger-km',
    vehicle_km: 'Vehicle-km',
    fte_hour: 'FTE working hour',
    night: 'Night',
    passenger_night: 'Passenger-night',
    meal: 'Meal',
    number: 'Item (count)',
    container_km: 'Container-km',
    tonne_km: 'Tonne-km',
    litre_km: 'Litre-km',
    m2_year: 'm²·year',
    gb_hour: 'GB·hour',
    km_hour: 'km·hour',
    kg_year: 'kg·year',
    number_year: 'Item·year',
    eur: 'Euro',
    usd: 'US dollar',
};
export function isOpaque(code) {
    return OPAQUE_UNIT_CODES.includes(code);
}
/**
 * Returns a display label for any supported unit code.
 * For library-known units, returns the first name (e.g., 'kg' → 'kilogram').
 * For opaque codes, returns the curated label above.
 */
export function unitLabel(code) {
    if (isOpaque(code))
        return OPAQUE_LABELS[code];
    const kind = getMeasureKind(code);
    if (kind === undefined)
        return code;
    const family = conversions
        .get(kind)
        ?.units.find((u) => u.names.includes(code) || u.symbols.includes(code));
    return family?.names[0] ?? code;
}
/**
 * Check whether two unit codes are compatible for conversion.
 *  - Same code: always compatible (identity).
 *  - Both opaque: compatible iff equal.
 *  - Both library-known: compatible iff same MeasureKind.
 *  - Mixed (one opaque, one library-known): not compatible.
 */
export function canConvert(from, to) {
    if (from === to)
        return true;
    if (isOpaque(from) || isOpaque(to))
        return false;
    const fromKind = getMeasureKind(from);
    const toKind = getMeasureKind(to);
    return fromKind !== undefined && fromKind === toKind;
}
export class IncompatibleUnitsError extends Error {
    from;
    to;
    constructor(from, to) {
        super(`Cannot convert ${from} to ${to}: incompatible units`);
        this.from = from;
        this.to = to;
        this.name = 'IncompatibleUnitsError';
    }
}
/**
 * Convert a numeric value from one unit to another.
 * Throws IncompatibleUnitsError when the codes don't share a MeasureKind.
 */
export function convertValue(value, from, to) {
    if (from === to)
        return value;
    if (!canConvert(from, to))
        throw new IncompatibleUnitsError(from, to);
    // Safe cast: canConvert already validated both are library-known + same kind.
    return convert(value, from).to(to);
}
/**
 * List the unit codes compatible with `from`, for the account-link dropdown.
 *  - Opaque: only [from] (no cross-opaque conversion).
 *  - Library-known: one entry per unit family in the same MeasureKind.
 *  - Unknown/empty: [from] as a pass-through.
 *
 * The returned `code` is what the dropdown writes to `multiplier_unit_code`;
 * `label` is the human display string.
 */
export function compatibleUnits(from) {
    if (isOpaque(from)) {
        return [{ code: from, label: OPAQUE_LABELS[from] }];
    }
    const kind = getMeasureKind(from);
    if (kind === undefined) {
        return [{ code: from, label: from }];
    }
    const families = conversions.get(kind)?.units ?? [];
    return families.map((u) => ({
        code: u.symbols[0] ?? u.names[0],
        label: u.names[0],
    }));
}
/**
 * Returns the MeasureKind for a code, or 'opaque' for opaque codes, or
 * undefined for totally unknown strings. Useful for UI messaging.
 */
export function measureKindOf(code) {
    if (isOpaque(code))
        return 'opaque';
    return getMeasureKind(code);
}
/**
 * Library unit names / symbols we intentionally exclude from the admin
 * dropdown, because they either (a) collide with an opaque domain code we
 * curate ourselves, or (b) are esoteric SI-prefix variants that clutter the
 * picker without any real-world use in emission accounting.
 *
 * Collision examples: the library exposes a Mass family whose primary name
 * is `"short ton"` (with a space) — the same physical unit as our opaque
 * `short_ton` (with an underscore). Keeping both in the dropdown would let
 * admins pick two codes for the same unit, and canConvert would treat them
 * as incompatible (library-known vs opaque). Exclude the library variant so
 * our opaque code is the single source of truth.
 */
const DROPDOWN_EXCLUDED_NAMES = new Set([
    // Collision with OPAQUE_UNIT_CODES.short_ton
    'short ton',
    'short tons',
    'US ton',
    'US tons',
    // Esoteric SI-prefix mass units no one enters by hand
    'petagram',
    'teragram',
    'gigagram',
    'megagram',
    'femtogram',
    'picogram',
    'nanogram',
    'microgram',
    // Esoteric mass symbols
    'Pg',
    'Tg',
    'Gg',
    'Mg',
    'fg',
    'pg',
    'ng',
    'µg',
    'μg',
]);
function isDropdownExcluded(code, label) {
    return DROPDOWN_EXCLUDED_NAMES.has(code) || DROPDOWN_EXCLUDED_NAMES.has(label);
}
export function allUnitsGrouped() {
    const groups = [];
    const kindOrder = [
        { kind: MeasureKind.Mass, label: 'Mass' },
        { kind: MeasureKind.Length, label: 'Length' },
        { kind: MeasureKind.Volume, label: 'Volume' },
        { kind: MeasureKind.Energy, label: 'Energy' },
        { kind: MeasureKind.Time, label: 'Time' },
    ];
    for (const { kind, label } of kindOrder) {
        const families = conversions.get(kind)?.units ?? [];
        if (families.length === 0)
            continue;
        const units = families
            .map((u) => ({
            code: u.symbols[0] ?? u.names[0],
            label: u.names[0],
        }))
            .filter((u) => !isDropdownExcluded(u.code, u.label));
        if (units.length === 0)
            continue;
        groups.push({ label, code: `kind-${kind}`, units });
    }
    groups.push({
        label: 'Domain / opaque',
        code: 'opaque',
        units: OPAQUE_UNIT_CODES.map((c) => ({ code: c, label: OPAQUE_LABELS[c] })),
    });
    return groups;
}
/**
 * Compose a `unit_of_measure` display string from input + output codes. Used
 * by the admin EmissionFactorDialog to keep the legacy display column in
 * sync with the typed fields.
 *
 *   ('kg', 'ton')       -> 'kg CO2e/ton'
 *   ('t',  'short_ton') -> 't CO2e/short ton'
 *   ('kg', null)        -> 'kg CO2e'
 *   ('g',  'km')        -> 'g CO2e/km'   (out-of-set codes render verbatim,
 *                          not as kg — they can arrive via external writers
 *                          like the bookkeeper's Climatiq co2e_unit)
 */
export function formatUnitOfMeasure(outputCode, inputCode) {
    let numerator;
    if (outputCode === 't') {
        numerator = 'MT CO2e';
    }
    else if (!outputCode || outputCode === 'kg') {
        numerator = 'kg CO2e';
    }
    else {
        numerator = `${outputCode} CO2e`;
    }
    if (!inputCode)
        return numerator;
    // Opaque codes use spaces in their display forms; library codes use their canonical.
    const denom = isOpaque(inputCode) ? inputCode.replace(/_/g, ' ') : inputCode;
    return `${numerator}/${denom}`;
}
//# sourceMappingURL=unitConversion.js.map